import { useState, useRef, useEffect } from "react";
import {
  ShoppingBag,
  Menu,
  X,
  ChevronDown,
  Flower2,
  Cake,
  Gem,
  GraduationCap,
  Baby,
  Smile,
  Briefcase,
  Ribbon,
  HeartHandshake,
  Rose,
  Leaf,
  Sun,
  Snowflake,
  Truck,
  Phone,
  Info,
  MapPin,
  Star,
  Gift,
  Heart,
  Flame,
  Sparkles,
  FileText,
  Sprout,
  Dog,
  Wind,
  Wine,
  Coffee,
  Candy,
  Package,
} from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useBouquetTypes } from "../../hooks/useBouquetTypes";
import { User } from "lucide-react";
import supabase from "../../lib/supabase";
import logoSvg from "../../assets/happy-day-flowers-logo.svg";

// Note: Lucide doesn't have Bear/Balloon — we use replacements below
import {
  ShoppingBag as ShoppingBagIcon,
  Menu as MenuIcon,
  X as XIcon,
  ChevronDown as ChevronDownIcon,
  Flower2 as Flower2Icon,
  Cake as CakeIcon,
  Gem as GemIcon,
  GraduationCap as GraduationCapIcon,
  Baby as BabyIcon,
  Smile as SmileIcon,
  Briefcase as BriefcaseIcon,
  Ribbon as RibbonIcon,
  HeartHandshake as HeartHandshakeIcon,
  Rose as RoseIcon,
  Leaf as LeafIcon,
  Sun as SunIcon,
  Snowflake as SnowflakeIcon,
  Truck as TruckIcon,
  Phone as PhoneIcon,
  Info as InfoIcon,
  MapPin as MapPinIcon,
  Star as StarIcon,
  Gift as GiftIcon,
  Heart as HeartIcon,
  Flame as FlameIcon,
  Candy as CandyIcon,
  FileText as FileTextIcon,
  Sprout as SproutIcon,
  Dog as DogIcon,
  Wind as WindIcon,
} from "lucide-react";

const navItems = [
  { label: "Home", page: "home", mega: null },
  {
    label: "Bouquets",
    page: "shop",
    mega: {
      heading: "Shop by Type",
      sub: "Find the perfect style for every occasion",

      cols: [
        {
          title: "Arrangement Styles",
          grid: 2,
          items: [
            { Icon: StarIcon,    label: "All Bouquets",       desc: "Browse our full collection",  bouquetType: null },
            { Icon: Package,     label: "Boxes",              desc: "Blooms in a beautiful box",   bouquetType: "Boxes" },
            { Icon: Flower2Icon, label: "Bouquets",           desc: "Hand-tied fresh flowers",     bouquetType: "Bouquets" },
            { Icon: Sparkles,    label: "Vases",              desc: "Ready to display, no fuss",   bouquetType: "Vase Arrangements" },
            { Icon: LeafIcon,    label: "Dried Arrangements", desc: "Long-lasting botanical art",  bouquetType: "Dried Arrangements" },
          ],
        },
      ],
      feature: null,
    },
  },

  {
    label: "Occasions",
    page: "shop",
    mega: {
      heading: "Shop by Occasion",
      sub: "The perfect arrangement for every moment",
      cols: [
        {
          title: "Celebrate",
          items: [
            { Icon: CakeIcon,          label: "Birthday",       desc: "Make their day magical",        category: "birthday" },
            { Icon: GemIcon,           label: "Anniversary",    desc: "Celebrate your love",           category: "anniversary" },
            { Icon: GraduationCapIcon, label: "Graduation",     desc: "Proud moments deserve blooms",  category: "graduation" },
            { Icon: BabyIcon,          label: "New Baby",       desc: "Welcome the little one",        category: "new baby" },
            { Icon: HeartIcon,         label: "Valentine's Day",desc: "Love in full bloom",             category: "valentine's day" },
            { Icon: RoseIcon,          label: "Mother's Day",   desc: "For the one who does it all",   category: "mother's day" },
          ],
        },
        {
          title: "Express",
          items: [
            { Icon: SmileIcon,        label: "Just Because",   desc: "No reason needed",              category: "just because" },
            { Icon: BriefcaseIcon,    label: "Corporate",      desc: "Impress with elegance",         category: "corporate" },
            { Icon: RibbonIcon,       label: "Sympathy",       desc: "Comfort in every petal",        category: "sympathy" },
            { Icon: HeartHandshakeIcon,label: "Romance",       desc: "Say it with flowers",           category: "romance" },
          ],
        },
      ],
      feature: null,
    },
  },

  {
    label: "Gifts",
    page: "gifts",
    mega: {
      heading: "Gift Add-Ons",
      sub: "Complete the moment with something extra special",
      // cols with `groups` = multiple labelled sections stacked in one column
      cols: [
        {
          groups: [
            {
              title: "Browse",
              items: [
                {
                  Icon: Gift,
                  label: "All Gifts",
                  desc: "View everything available",
                  page: "gifts",
                },
              ],
            },
            {
              title: "Sweet & Fun",
              items: [
                {
                  Icon: Candy,
                  label: "Chocolates",
                  desc: "Sweet indulgence",
                  category: "chocolates",
                },
                {
                  Icon: Sparkles,
                  label: "Teddy Bears",
                  desc: "Soft & cuddly",
                  category: "teddy",
                },
                {
                  Icon: Sun,
                  label: "Balloons",
                  desc: "Add some fun",
                  category: "balloons",
                },
                {
                  Icon: Package,
                  label: "Combos",
                  desc: "Bundle & save",
                  category: "combos",
                },
              ],
            },
          ],
        },
        {
          groups: [
            {
              title: "Thoughtful",
              items: [
                {
                  Icon: Flame,
                  label: "Candles",
                  desc: "Luxury home fragrance",
                  category: "candles",
                },
                {
                  Icon: FileText,
                  label: "Cards",
                  desc: "Personalised message",
                  category: "cards",
                },
                {
                  Icon: Sprout,
                  label: "Plants",
                  desc: "Long-lasting gift",
                  category: "plants",
                },
                {
                  Icon: Coffee,
                  label: "Cup",
                  desc: "A gift for every morning",
                  category: "cup",
                },
                {
                  Icon: Wine,
                  label: "Wine",
                  desc: "Celebrate in style",
                  category: "wine",
                },
              ],
            },
          ],
        },
      ],
      feature: {
        label: "Most Gifted",
        name: "Luxe Gift Bundle",
        desc: "Flowers, chocolates & a handwritten note — all in one.",
        price: "$120",
        image:
          "https://images.pexels.com/photos/5743922/pexels-photo-5743922.jpeg?auto=compress&cs=tinysrgb&w=600",
        category: "gift-box",
      },
    },
  },

  {
    label: "Info",
    page: null,
    mega: {
      heading: "Happy Day Flowers",
      sub: "Everything you need to know",
      simple: true,
      cols: [
        {
          title: "About Us",
          items: [
            {
              Icon: LeafIcon,
              label: "Our Story",
              desc: "How we began",
              page: "about",
            },
            {
              Icon: StarIcon,
              label: "Why Choose Us",
              desc: "What makes us special",
              page: "why-choose-us",
            },
            {
              Icon: MapPinIcon,
              label: "Our Studio",
              desc: "Visit Travancore",
              page: "our-studio",
            },
          ],
        },
        {
          title: "Delivery",
          items: [
            {
              Icon: TruckIcon,
              label: "Delivery Info",
              desc: "Same-day Melbourne",
              page: "delivery",
            },
            {
              Icon: MapPinIcon,
              label: "Delivery Zones",
              desc: "Where we deliver",
              page: "delivery-zones",
            },
            {
              Icon: GiftIcon,
              label: "Gift Messages",
              desc: "Free with every order",
              page: "gift-messages",
            },
          ],
        },
        {
          title: "Get in Touch",
          items: [
            {
              Icon: PhoneIcon,
              label: "Contact Us",
              desc: "We'd love to hear",
              page: "contact",
            },
            {
              Icon: HeartIcon,
              label: "Custom Orders",
              desc: "Bespoke arrangements",
              page: "custom-orders",
            },
            {
              Icon: InfoIcon,
              label: "FAQs",
              desc: "Quick answers",
              page: "faqs",
            },
          ],
        },
      ],
    },
  },
];

// Icon mapping for known bouquet types
const TYPE_ICONS = {
  spring: SunIcon,
  summer: FlameIcon,
  autumn: LeafIcon,
  winter: SnowflakeIcon,
  dried: SnowflakeIcon,
  native: SproutIcon,
  roses: RoseIcon,
  tropical: WindIcon,
  wedding: HeartIcon,
  sympathy: RibbonIcon,
};

export default function Navbar() {
  const { cartCount, setCartOpen } = useCart();
  const { page, navigate, setTypeFilter, mobileMenuOpen, setMobileMenuOpen } =
    useApp();
  const { user, displayName, setAuthModalOpen, setAccountPanelOpen } =
    useAuth();
  const bouquetTypes = useBouquetTypes();
  const [openMega, setOpenMega] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [featuredGift, setFeaturedGift] = useState(null);
  const [featuredBouquet, setFeaturedBouquet] = useState(null);
  const [featuredBouquet2, setFeaturedBouquet2] = useState(null);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  // Fetch most-ordered gift for the Gifts feature card
  useEffect(() => {
    Promise.all([
      supabase
        .from("gift")
        .select("id, name, description, price, type, image_urls")
        .eq("active", true),
      supabase.from("order_addon").select("addon_id"),
    ]).then(([{ data: gifts }, { data: orders }]) => {
      if (!gifts?.length) return;
      const counts = {};
      for (const row of orders ?? []) {
        counts[row.addon_id] = (counts[row.addon_id] ?? 0) + 1;
      }
      const ranked = [...gifts].sort(
        (a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0),
      );
      const pick = ranked.find((g) => g.image_urls?.length);
      if (pick) setFeaturedGift(pick);
    });
  }, []);

  // Fetch top-2 most-ordered bouquets: #1 for Occasions, #2 for Bouquets menu
  useEffect(() => {
    supabase
      .from("bouquets")
      .select("id, name, description, price, occasions, images, ordered")
      .eq("active", true)
      .then(({ data: bouquets }) => {
        if (!bouquets?.length) return;
        const sorted = [...bouquets]
          .sort((a, b) => (b.ordered ?? 0) - (a.ordered ?? 0))
          .filter((b) => b.images?.length);
        if (sorted[0]) setFeaturedBouquet(sorted[0]);
        if (sorted[1]) setFeaturedBouquet2(sorted[1]);
      });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMega(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock body scroll when mobile menu is open (iOS-safe: fix both html + body)
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = Math.abs(parseInt(document.body.style.top || "0"));
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [mobileMenuOpen]);

  const handleNavigate = (p, category) => {
    navigate(p, category);
    setOpenMega(null);
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  };

  const handleMouseEnter = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMega(label);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMega(null), 80);
  };

  // Build dynamic Gifts mega — curated 2-col layout, feature card from real data
  const staticGiftsCols = navItems.find((i) => i.label === "Gifts").mega.cols;
  const giftsMega = {
    ...navItems.find((i) => i.label === "Gifts").mega,
    cols: staticGiftsCols, // keep the curated Sweet & Fun / Thoughtful grouping
    feature: featuredGift
      ? {
          label: "Most Gifted",
          name: featuredGift.name,
          desc:
            featuredGift.description?.slice(0, 60) +
            (featuredGift.description?.length > 60 ? "…" : ""),
          price: `$${Number(featuredGift.price).toFixed(0)}`,
          image: featuredGift.image_urls?.[0] ?? "",
          category: featuredGift.type,
        }
      : navItems.find((i) => i.label === "Gifts").mega.feature,
  };

  // Build dynamic Seasons mega from bouquet_type values (kept for handleNavigateType, not rendered)
  const seasonsMega = {
    heading: "Shop by Type",
    sub: "Browse by style, season or flower kind",
    cols: [
      {
        title: "Bouquet Types",
        items: [
          { Icon: Flower2Icon, label: "Bouquets", desc: "All flower arrangements", bouquetType: null },
          ...bouquetTypes.map((t) => {
            const key = t.toLowerCase();
            const Icon = TYPE_ICONS[key] ?? Flower2Icon;
            return { Icon, label: t, desc: "", bouquetType: t };
          }),
        ],
      },
    ],
    feature: null,
  };

  const handleNavigateType = (p, bouquetType) => {
    navigate(p);
    setTypeFilter(bouquetType);
    setOpenMega(null);
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  };

  const makeFeatureCard = (b, label) => b ? {
    label,
    name: b.name,
    desc: (b.description ?? "").slice(0, 60) + ((b.description?.length ?? 0) > 60 ? "…" : ""),
    price: `$${Number(b.price).toFixed(0)}`,
    image: b.images?.[0] ?? "",
    category: b.occasions?.[0] ?? "all",
  } : null;

  const bouquetFeatureCard  = makeFeatureCard(featuredBouquet,  "Best Seller");
  const bouquetFeatureCard2 = makeFeatureCard(featuredBouquet2, "This Week's Pick");

  const resolvedNavItems = navItems.map((i) => {
    if (i.label === "Gifts") return { ...i, mega: giftsMega };
    if (i.label === "Occasions") return { ...i, mega: { ...i.mega, feature: bouquetFeatureCard } };
    if (i.label === "Bouquets") return { ...i, mega: { ...i.mega, feature: bouquetFeatureCard2 } };
    return i;
  });

  const activeMega = resolvedNavItems.find((i) => i.label === openMega);

  return (
    <nav ref={navRef} className="w-full z-50">
      {/* ── Main bar — solid white, no transparency ── */}
      <div className="bg-brand-cream-50 border-b border-brand-cream-300 shadow-sm relative">
        {/* Ambient bleed from hero top */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none z-0 transition-all duration-700"
          style={{
            background:
              "linear-gradient(to top, var(--ambient-top-color, transparent), transparent)",
            opacity: 0.35,
          }}
        />
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between relative z-10">
          {/* Logo */}

          <button
            onClick={() => handleNavigate("home")}
            className="flex items-center group flex-shrink-0"
          >
            <img
              src={logoSvg}
              alt="Happy Day Flowers"
              className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </button>

          {/* Desktop nav links */}
          <ul className="hidden md:flex items-center gap-0.5">
            {resolvedNavItems.map((item) => (
              <li key={item.label}>
                <button
                  onClick={() => {
                    if (!item.mega) handleNavigate(item.page);
                  }}
                  onMouseEnter={() =>
                    item.mega ? handleMouseEnter(item.label) : setOpenMega(null)
                  }
                  onMouseLeave={() => item.mega && handleMouseLeave()}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm
                              tracking-wide transition-all duration-200 select-none
                    ${
                      openMega === item.label
                        ? "text-brand-crimson-500 bg-brand-cream-100"
                        : page === item.page && item.page
                          ? "text-brand-crimson-400 bg-brand-cream-100/70"
                          : "text-brand-forest-500 hover:text-brand-forest-700 hover:bg-brand-forest-50/60"
                    }`}
                >
                  {item.label}
                  {item.mega && (
                    <ChevronDownIcon
                      className={`w-3 h-3 transition-transform duration-200
                      ${openMega === item.label ? "rotate-180" : ""}`}
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <div
              className="hidden lg:flex items-center gap-1.5 bg-brand-forest-50 border border-brand-forest-100
                            px-3 py-1.5 rounded-full"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-forest-400 animate-pulse" />
              <span className="font-body text-xs text-brand-forest-500">
                Same-day delivery
              </span>
            </div>

            {/* ── Auth button ── */}
            <div className="hidden md:block">
              {user ? (
                <button
                  onClick={() => setAccountPanelOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-brand-cream-100 border border-brand-cream-200 transition-colors group"
                >
                  <div className="w-6 h-6 rounded-full bg-brand-forest-500 flex items-center justify-center">
                    <span className="font-body text-[10px] font-bold text-white uppercase">
                      {displayName?.[0] ?? "U"}
                    </span>
                  </div>
                  <span className="font-body text-xs text-brand-forest-600 max-w-24 truncate">
                    {displayName}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setAuthModalOpen(true)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-brand-forest-300 hover:border-brand-crimson-400 text-brand-forest-600 hover:text-brand-crimson-500 font-body text-xs transition-all"
                >
                  <User className="w-3.5 h-3.5" /> Sign In
                </button>
              )}
            </div>

            <button
              onClick={() => setCartOpen(true)}
              aria-label={cartCount > 0 ? `Open cart, ${cartCount} item${cartCount !== 1 ? "s" : ""}` : "Open cart"}
              className="relative p-3 rounded-full hover:bg-brand-cream-100 transition-colors group"
            >
              <ShoppingBagIcon className="w-5 h-5 text-brand-forest-500 group-hover:text-brand-crimson-400 transition-colors" />
              {cartCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 bg-brand-crimson-400 text-white text-[10px]
                                 w-4 h-4 rounded-full flex items-center justify-center
                                 font-body font-semibold"
                >
                  {cartCount}
                </span>
              )}
            </button>

            <button
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              className="md:hidden p-3 rounded-full hover:bg-brand-cream-100 transition-colors"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setMobileExpanded(null);
              }}
            >
              <span className="relative w-5 h-5 flex items-center justify-center">
                <XIcon
                  className={`absolute w-5 h-5 text-brand-forest-500 transition-all duration-200
                  ${mobileMenuOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90"}`}
                />
                <MenuIcon
                  className={`absolute w-5 h-5 text-brand-forest-500 transition-all duration-200
                  ${mobileMenuOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"}`}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mega menu panel ── */}
      {activeMega?.mega && (
        <div
          onMouseEnter={() => handleMouseEnter(activeMega.label)}
          onMouseLeave={handleMouseLeave}
          className="hidden md:block absolute top-full left-0 w-full bg-brand-cream-50
                     border-b border-brand-cream-300 shadow-xl
                     animate-in fade-in slide-in-from-top-1 duration-150"
        >
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="mb-6">
              <p className="font-display text-brand-forest-700 text-lg leading-none">
                {activeMega.mega.heading}
              </p>
              <p className="font-body text-brand-forest-400 text-xs mt-1">
                {activeMega.mega.sub}
              </p>
            </div>

            <div
              className="grid gap-8"
              style={{
                gridTemplateColumns:
                  activeMega.mega.feature || activeMega.mega.simple
                    ? `repeat(${activeMega.mega.cols.length}, 1fr) 240px`
                    : `repeat(${activeMega.mega.cols.length}, 1fr)`,
              }}
            >
              {activeMega.mega.cols.map((col, ci) => {
                // Normalise: cols may have `groups` (multi-section) or legacy `items` (single section)
                const groups = col.groups ?? [
                  { title: col.title, items: col.items ?? [] },
                ];
                return (
                  <div key={ci} className="flex flex-col gap-5 self-start">
                    {groups.map((group, gi) => (
                      <div key={group.title}>
                        <p className="font-body text-[10px] text-brand-crimson-400 tracking-[0.2em] uppercase mb-2 font-semibold">
                          {group.title || ' '}
                        </p>
                        {/* Items wrap sideways; when space runs out they stack */}
                        <ul className={group.grid ? `grid grid-cols-${group.grid} gap-1` : "flex flex-wrap gap-0.5"}>
                          {group.items.map((link) => (
                            <li key={link.label} className={group.grid ? "" : "w-full"}>
                              <button
                                onClick={() =>
                                  "bouquetType" in link
                                    ? handleNavigateType(
                                        activeMega.page,
                                        link.bouquetType,
                                      )
                                    : handleNavigate(
                                        link.page || activeMega.page,
                                        link.category,
                                      )
                                }
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                                           hover:bg-brand-cream-100 group transition-all duration-150 text-left"
                              >
                                <div
                                  className="w-8 h-8 rounded-lg bg-brand-cream-100 group-hover:bg-brand-cream-200
                                                flex items-center justify-center flex-shrink-0 transition-colors duration-150"
                                >
                                  <link.Icon className="w-3.5 h-3.5 text-brand-forest-400 group-hover:text-brand-crimson-500 transition-colors duration-150" />
                                </div>
                                <div>
                                  <p
                                    className="font-body text-sm text-brand-forest-700 group-hover:text-brand-crimson-500
                                                transition-colors font-medium leading-tight"
                                  >
                                    {link.label}
                                  </p>
                                  <p className="font-body text-xs text-brand-forest-400 leading-tight mt-0.5">
                                    {link.desc}
                                  </p>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                        {/* Divider between groups within the same column, not after last */}
                        {gi < groups.length - 1 && (
                          <div className="mt-4 border-t border-brand-cream-200" />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}

              {/* Feature card */}
              {activeMega.mega.feature && (
                <div
                  className="relative rounded-2xl overflow-hidden cursor-pointer group
                             shadow-sm hover:shadow-lg transition-all duration-300"
                  onClick={() =>
                    handleNavigate(
                      activeMega.page,
                      activeMega.mega.feature.category,
                    )
                  }
                >
                  <img
                    src={activeMega.mega.feature.image}
                    alt={activeMega.mega.feature.name}
                    className="absolute inset-0 w-full h-full object-cover
                               group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-brand-forest-900/85
                                  via-brand-forest-900/20 to-transparent"
                  />
                  <div className="relative p-5 flex flex-col justify-end h-full min-h-[210px]">
                    <span
                      className="absolute top-4 left-4 bg-brand-crimson-400 text-white
                                     font-body text-[10px] px-2.5 py-1 rounded-full"
                    >
                      {activeMega.mega.feature.label}
                    </span>
                    <p className="font-display text-white text-lg leading-tight">
                      {activeMega.mega.feature.name}
                    </p>
                    <p className="font-body text-white/70 text-xs mt-1 mb-3 leading-snug">
                      {activeMega.mega.feature.desc}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-brand-crimson-200 text-base">
                        {activeMega.mega.feature.price}
                      </span>
                      <span
                        className="font-body text-white/80 text-xs border border-white/30
                                       px-3 py-1 rounded-full group-hover:bg-brand-crimson-400
                                       group-hover:border-brand-crimson-400 transition-colors"
                      >
                        Order Now →
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Info panel */}
              {activeMega.mega.simple && (
                <div
                  className="flex flex-col justify-center items-center
                                bg-gradient-to-br from-brand-cream-50 to-brand-cream-100
                                rounded-2xl p-6 border border-brand-cream-200"
                >
                  <div className="mb-3">
                    <img
                      src={logoSvg}
                      alt="Happy Day Flowers"
                      className="h-12 w-auto mx-auto"
                    />
                  </div>
                  <p className="font-display text-brand-forest-700 text-base text-center mb-1">
                    Melbourne's florist
                  </p>
                  <p
                    className="font-body text-brand-forest-400 text-xs text-center
                                leading-relaxed mb-4 max-w-[160px]"
                  >
                    Handcrafted daily in our Travancore studio since 2018.
                  </p>
                  <button
                    onClick={() => handleNavigate("contact")}
                    className="bg-brand-crimson-500 hover:bg-brand-crimson-600 text-white font-body
                               text-xs px-5 py-2 rounded-full transition-colors shadow-sm"
                  >
                    Get in Touch
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile menu ── */}
      <div
        className={`md:hidden bg-brand-cream-50 border-t border-brand-cream-300 flex flex-col overflow-hidden
                    transition-all duration-300 ease-out
                    ${mobileMenuOpen ? "max-h-[calc(100dvh-4rem)] opacity-100" : "max-h-0 opacity-0"}`}
      >
        {/* Scrollable nav items */}
        <div
          className="overflow-y-auto overscroll-contain flex-1 min-h-0"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {resolvedNavItems.map((item) => (
            <div
              key={item.label}
              className="border-b border-brand-cream-200 last:border-0"
            >
              <button
                onClick={() => {
                  if (!item.mega) handleNavigate(item.page);
                  else
                    setMobileExpanded(
                      mobileExpanded === item.label ? null : item.label,
                    );
                }}
                className="w-full flex items-center justify-between px-6 py-4 text-left
                           active:bg-brand-cream-100 transition-colors"
              >
                <span
                  className={`font-body text-sm font-medium transition-colors
                  ${page === item.page && item.page ? "text-brand-crimson-500" : "text-brand-forest-600"}`}
                >
                  {item.label}
                </span>
                {item.mega && (
                  <ChevronDownIcon
                    className={`w-4 h-4 text-brand-forest-400 transition-transform duration-200
                    ${mobileExpanded === item.label ? "rotate-180" : ""}`}
                  />
                )}
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-out
                  ${mobileExpanded === item.label ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="bg-brand-cream-50/80 px-4 pb-4">
                  {item.mega?.cols.map((col, ci) => {
                    const groups = col.groups ?? [
                      { title: col.title, items: col.items ?? [] },
                    ];
                    return groups.map((group) => (
                      <div
                        key={`${ci}-${group.title}`}
                        className="mb-4 first:mt-2"
                      >
                        <p className="font-body text-[10px] text-brand-crimson-400 tracking-[0.2em] uppercase px-2 mb-2 font-semibold">
                          {group.title}
                        </p>
                        {group.items.map((link) => (
                          <button
                            key={link.label}
                            onClick={() =>
                              "bouquetType" in link
                                ? handleNavigateType(
                                    item.page,
                                    link.bouquetType,
                                  )
                                : handleNavigate(
                                    link.page || item.page,
                                    link.category,
                                  )
                            }
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                                       hover:bg-brand-cream-100 active:bg-brand-cream-200 text-left transition-colors"
                          >
                            <div
                              className="w-7 h-7 rounded-lg bg-brand-cream-50 border border-brand-cream-200
                                            flex items-center justify-center flex-shrink-0"
                            >
                              <link.Icon className="w-3.5 h-3.5 text-brand-forest-400" />
                            </div>
                            <div>
                              <p className="font-body text-sm text-brand-forest-600 font-medium leading-tight">
                                {link.label}
                              </p>
                              <p className="font-body text-xs text-brand-forest-400 leading-tight">
                                {link.desc}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    ));
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pinned auth + CTA — always visible at bottom */}
        <div className="shrink-0 px-6 py-4 flex flex-col gap-3 bg-gradient-to-br from-brand-cream-100/50 to-brand-cream-200/50 border-t border-brand-cream-200">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-forest-400 animate-pulse" />
            <span className="font-body text-xs text-brand-forest-500">
              Same-day Melbourne delivery available
            </span>
          </div>

          {user ? (
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setAccountPanelOpen(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border border-brand-forest-200 bg-white/70 active:bg-brand-cream-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-brand-forest-500 flex items-center justify-center shrink-0">
                <span className="font-body text-xs font-bold text-white uppercase">
                  {displayName?.[0] ?? "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-body text-sm font-semibold text-brand-forest-700 truncate">
                  {displayName}
                </p>
                <p className="font-body text-[11px] text-brand-forest-400">
                  My Account
                </p>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-brand-forest-300 -rotate-90 shrink-0" />
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setAuthModalOpen(true);
                }}
                className="font-body text-sm py-3 rounded-full border border-brand-forest-300 text-brand-forest-600 active:bg-brand-cream-100 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setAuthModalOpen(true);
                }}
                className="font-body text-sm py-3 rounded-full bg-brand-crimson-500 text-white active:bg-brand-crimson-600 transition-colors shadow-sm"
              >
                Sign Up
              </button>
            </div>
          )}

          <button
            onClick={() => handleNavigate("shop")}
            className="w-full bg-brand-forest-500 hover:bg-brand-forest-600 active:bg-brand-forest-700
                       text-white font-body text-sm py-3 rounded-full transition-colors shadow-sm"
          >
            Shop Now
          </button>
        </div>
      </div>
    </nav>
  );
}
